<template>
  <Transition name="phone-code-dialog">
    <div v-if="isOpen"
         class="phone-code-dialog-overlay"
         @click="handleClose">
      <div class="phone-code-dialog-card"
           @click.stop>
        <div class="phone-code-dialog-header">
          <div class="phone-code-dialog-title">{{ t('phone.codeDialog.title') }}</div>
          <button type="button"
                  class="phone-code-dialog-close"
                  @click="handleClose">
            <svg width="24"
                 height="24"
                 viewBox="0 0 24 24"
                 fill="none"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round" />
            </svg>
          </button>
        </div>

        <div class="phone-code-dialog-content">
          <div class="phone-code-dialog-search">
            <svg class="phone-code-dialog-search-icon"
                 width="20"
                 height="20"
                 viewBox="0 0 24 24"
                 fill="none"
                 xmlns="http://www.w3.org/2000/svg">
              <circle cx="11"
                      cy="11"
                      r="8"
                      stroke="currentColor"
                      stroke-width="2" />
              <path d="m21 21-4.35-4.35"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round" />
            </svg>
            <input :value="searchQuery"
                   @input="handleSearchUpdate"
                   type="text"
                   :placeholder="t('phone.codeDialog.searchPlaceholder')"
                   class="phone-code-dialog-search-input" />
            <button v-if="searchQuery"
                    type="button"
                    class="phone-code-dialog-search-clear"
                    @click="handleClearSearch">
              <svg width="20"
                   height="20"
                   viewBox="0 0 24 24"
                   fill="none"
                   xmlns="http://www.w3.org/2000/svg">
                <path d="m15 9-6 6M9 9l6 6"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round" />
              </svg>
            </button>
          </div>

          <div class="phone-code-selector-list">
            <div v-for="(item, i) in filteredPhoneCodes"
                 :key="i"
                 @click="handleSelect(item)"
                 class="phone-code-item">
              <FlagIcon :iso-code="item.isoCode"
                        :width="28"
                        :border-radius="4"
                        class="phone-code-item-flag" />
              <div class="phone-code-item-content">
                <span class="country-name-text">{{ getDisplayName(item) }}</span>
                <span class="phone-code">+{{ item.phoneCode }}</span>
              </div>
            </div>
            <div v-if="hasNoResults"
                 class="phone-code-item phone-code-item-empty">
              {{ t('phone.codeDialog.empty') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { type PhoneCode } from '@/constants/phone-codes'
import FlagIcon from './FlagIcon.vue'
import { usePhoneCodeLocalization, usePhoneCodeFilter } from '@/composables/phone'

interface Props {
  modelValue: boolean
  searchQuery?: string
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'update:searchQuery', value: string): void
  (e: 'select', value: PhoneCode): void
}

const props = withDefaults(defineProps<Props>(), {
  searchQuery: '',
})

const emit = defineEmits<Emits>()

const { t } = useI18n()
const { getDisplayName } = usePhoneCodeLocalization()

const searchQueryRef = computed(() => props.searchQuery)
const { filteredPhoneCodes, hasNoResults } = usePhoneCodeFilter(searchQueryRef)

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

const handleSearchUpdate = (event: Event) => {
  const target = event.target as HTMLInputElement
  emit('update:searchQuery', target.value)
}

const handleClearSearch = () => {
  emit('update:searchQuery', '')
}

const handleClose = () => {
  emit('update:searchQuery', '')
  isOpen.value = false
}

const handleSelect = (country: PhoneCode) => {
  emit('select', country)
  emit('update:searchQuery', '')
  isOpen.value = false
}
</script>

<style scoped>
.phone-code-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  transition: opacity 0.2s ease;
}

.phone-code-dialog-card {
  background: #fff;
  border-radius: 8px;
  width: 400px;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

/* Анимация появления/скрытия диалога */
.phone-code-dialog-enter-active,
.phone-code-dialog-leave-active {
  transition: opacity 0.2s ease;
}

.phone-code-dialog-enter-active .phone-code-dialog-card,
.phone-code-dialog-leave-active .phone-code-dialog-card {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.phone-code-dialog-enter-from,
.phone-code-dialog-leave-to {
  opacity: 0;
}

.phone-code-dialog-enter-from .phone-code-dialog-card,
.phone-code-dialog-leave-to .phone-code-dialog-card {
  transform: scale(0.96);
  opacity: 0;
}

.phone-code-dialog-header {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--color-grey-light, #C1C1C1);
}

.phone-code-dialog-title {
  margin: 0;
  font-family: var(--font-inter);
  font-size: 24px;
  font-weight: 300;
  color: #000;
}

.phone-code-dialog-close {
  background: none;
  border: none;
  color: #000;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
}

.phone-code-dialog-close:hover {
  opacity: 0.8;
}

.phone-code-dialog-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 90vh;
}

.phone-code-dialog-search {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid var(--color-grey-light, #C1C1C1);
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 16px;
}

.phone-code-dialog-search-icon {
  color: var(--color-grey, #868D98);
  margin-right: 8px;
  flex-shrink: 0;
}

.phone-code-dialog-search-input {
  flex: 1;
  border: none;
  outline: none;
  font-family: var(--font-inter);
  font-size: 16px;
  color: var(--color-dark-blue, #252840);
  min-height: 40px;
}

.phone-code-dialog-search-input::placeholder {
  color: var(--color-grey, #868D98);
}

.phone-code-dialog-search-clear {
  background: none;
  border: none;
  color: var(--color-grey, #868D98);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
}

.phone-code-selector-list {
  flex: 1;
  overflow-y: auto;
  min-height: 200px;
  max-height: calc(90vh - 180px);
}

.phone-code-item {
  display: flex;
  align-items: center;
  padding: 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  min-height: 56px;
}

.phone-code-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.phone-code-item-empty {
  justify-content: center;
  color: var(--color-grey, #868D98);
  cursor: default;
}

.phone-code-item-empty:hover {
  background-color: transparent;
}

.phone-code-item-flag {
  margin-right: 12px;
  flex-shrink: 0;
  /* border-radius: 16px; */
  /* box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1); */
}

.phone-code-item-content {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 4px;
}

.country-name-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 18px;
  font-family: var(--font-inter);
  color: var(--color-dark-blue, #252840);
}

.phone-code {
  font-weight: 500;
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 18px;
  font-family: var(--font-inter);
  color: var(--color-dark-blue, #252840);
}
</style>
